<template>
  <b-sidebar
    id="view-sales-type-sidebar"
    :visible="!!selectedSalesTypeId"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(val) => !val && $emit('close')"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">View Sales Type</h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <b-overlay :show="isLoading" spinner-variant="primary">
        <div class="p-2">
          <!-- Sales Type Details -->
          <div v-if="salesTypeData" class="sales-type-details">
            <!-- ID -->
            <div class="detail-row mb-3">
              <label class="detail-label font-weight-bold">ID:</label>
              <span class="detail-value">{{ salesTypeData.id }}</span>
            </div>

            <!-- Name -->
            <div class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Name:</label>
              <span class="detail-value">{{ salesTypeData.name }}</span>
            </div>

            <!-- Code -->
            <div class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Code:</label>
              <span class="detail-value">{{ salesTypeData.code }}</span>
            </div>

            <!-- Prefix -->
            <div class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Prefix:</label>
              <span class="detail-value">{{ salesTypeData.prefix }}</span>
            </div>

            <!-- Status -->
            <div class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Status:</label>
              <b-badge class="detail-value">
                {{ salesTypeData.isActive ? 'Active' : 'Inactive' }}
              </b-badge>
            </div>

            <!-- Business ID -->
            <div class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Business ID:</label>
              <span class="detail-value">{{ salesTypeData.businessID }}</span>
            </div>

            <!-- Created By -->
            <div class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Created By:</label>
              <span class="detail-value">{{
                salesTypeData.metaAddedBy || salesTypeData.addedBy || 'N/A'
              }}</span>
            </div>

            <!-- Created On -->
            <div class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Created On:</label>
              <span class="detail-value">{{ formatDate(salesTypeData.addedOn) }}</span>
            </div>

            <!-- Updated By -->
            <div class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Updated By:</label>
              <span class="detail-value">{{
                salesTypeData.metaUpdatedBy || salesTypeData.updatedBy || 'N/A'
              }}</span>
            </div>

            <!-- Updated On -->
            <div class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Updated On:</label>
              <span class="detail-value">{{ formatDate(salesTypeData.updatedOn) }}</span>
            </div>
          </div>

          <!-- No Data Message -->
          <div v-else-if="!isLoading" class="text-center text-muted">
            <feather-icon icon="AlertCircleIcon" size="48" class="mb-2" />
            <p>No sales type data found</p>
          </div>

          <!-- Action Buttons -->
          <div class="text-center mt-4">
            <b-button variant="outline-secondary" @click="hide"> Close </b-button>
          </div>
        </div>
      </b-overlay>
    </template>
  </b-sidebar>
</template>

<script>
import { BSidebar, BOverlay, BButton, BBadge } from 'bootstrap-vue';
import { ref, watch } from '@vue/composition-api';
import store from '@/store';

export default {
  name: 'SalesTypeListView',
  components: {
    BSidebar,
    BOverlay,
    BButton,
    BBadge,
  },
  props: {
    selectedSalesTypeId: {
      type: [Number],
      default: null,
    },
  },
  setup(props, { emit }) {
    const isLoading = ref(false);
    const salesTypeData = ref(null);

    const fetchSalesTypeData = async () => {
      if (!props.selectedSalesTypeId) return;

      isLoading.value = true;
      try {
        const response = await store.dispatch('fbr-invoice-sales-type/fetchSalesType', {
          id: props.selectedSalesTypeId,
        });

        console.log('=== SalesTypeListView fetchSalesTypeData response:', response);

        // Handle different possible response structures
        let fetchedSalesType = null;

        if (response && response.salesType) {
          // If response has salesType wrapper
          fetchedSalesType = response.salesType;
        } else if (response && response.id) {
          // If response is the direct salesType object
          fetchedSalesType = response;
        } else if (response && response.data && response.data.salesType) {
          // If response has nested data structure
          fetchedSalesType = response.data.salesType;
        } else if (response && response.data && response.data.id) {
          // If response.data is the direct salesType object
          fetchedSalesType = response.data;
        } else {
          console.error('Unexpected response structure:', response);
          throw new Error('Invalid response structure from API');
        }

        salesTypeData.value = fetchedSalesType;
        console.log('=== SalesTypeListView populated data:', salesTypeData.value);
      } catch (error) {
        console.error('Error fetching sales type:', error);
        salesTypeData.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleString();
      } catch (error) {
        return dateString;
      }
    };

    // Watch for selectedSalesTypeId changes to fetch data
    watch(
      () => props.selectedSalesTypeId,
      (newId) => {
        if (newId) {
          fetchSalesTypeData();
        } else {
          salesTypeData.value = null;
        }
      },
      { immediate: true }
    );

    return {
      salesTypeData,
      isLoading,
      formatDate,
    };
  },
};
</script>

<style lang="scss">
#view-sales-type-sidebar {
  .content-sidebar-header {
    padding: 1rem 1.5rem !important;
  }

  .sales-type-details {
    .detail-row {
      display: flex;
      align-items: flex-start;

      .detail-label {
        min-width: 120px;
        margin-right: 1rem;
        color: #6e6b7b;
      }

      .detail-value {
        flex: 1;
        word-break: break-word;
      }
    }
  }
}
</style>
